"use client";

import { useEffect, useRef, useState } from "react";
import { track } from "@/lib/analytics";
import { getChatContext } from "@/lib/pageContext";
import { pickStarters, GENERIC_STARTERS } from "@/lib/chatStarters";

type Msg = { role: "user" | "assistant"; content: string };

/** Marker the model appends to an answer with 2-3 suggested next questions.
 *  Stripped from the visible answer; rendered as tappable chips instead. */
const FU_MARKER = "@@FOLLOWUPS@@";

/** Split a (possibly mid-stream) answer into visible text + follow-up questions.
 *  Hides a partially-streamed marker so the user never sees "@@FOLLO…". */
function splitFollowups(text: string): { text: string; followups: string[] } {
  const i = text.indexOf(FU_MARKER);
  if (i === -1) {
    const partial = text.match(/@@F?O?L?L?O?W?U?P?S?@?@?$/);
    const clean = partial ? text.slice(0, text.length - partial[0].length) : text;
    return { text: clean.replace(/\s+$/, ""), followups: [] };
  }
  const followups = text
    .slice(i + FU_MARKER.length)
    .split("|")
    .map((s) => s.trim())
    .filter((s) => s.length > 1 && s.length <= 80)
    .slice(0, 3);
  return { text: text.slice(0, i).replace(/\s+$/, ""), followups };
}

/**
 * Floating AI help chat (bottom-right, site-wide). Streams from /api/chat
 * (worker → Anthropic). Session-only history; YMYL guardrails live in the
 * worker's system prompt.
 */
export function HelpChat() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<Msg[]>([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);
  /** Context-aware empty-state prompts, recomputed each time the panel opens. */
  const [starters, setStarters] = useState<string[]>(GENERIC_STARTERS);
  /** Model-suggested next questions shown after the latest answer. */
  const [followups, setFollowups] = useState<string[]>([]);
  /** A page form field has focus — on small screens the launcher gets out of
   *  the way so it never overlaps inputs during form entry (builder, mobile). */
  const [formFocus, setFormFocus] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [messages, open]);

  useEffect(() => {
    const isFormField = (t: EventTarget | null) =>
      t instanceof HTMLElement && !!t.closest("input, textarea, select, [contenteditable]");
    const onFocusIn = (e: FocusEvent) => setFormFocus(isFormField(e.target));
    const onFocusOut = () => setFormFocus(false);
    document.addEventListener("focusin", onFocusIn);
    document.addEventListener("focusout", onFocusOut);
    return () => {
      document.removeEventListener("focusin", onFocusIn);
      document.removeEventListener("focusout", onFocusOut);
    };
  }, []);

  // Refresh starters from the current page/journey context whenever the panel
  // opens (the user may have navigated since it was last open).
  useEffect(() => {
    if (open) setStarters(pickStarters(getChatContext()));
  }, [open]);

  function toggle() {
    setOpen((v) => {
      if (!v) track("help_chat_opened", {});
      return !v;
    });
  }

  async function send(text: string) {
    const content = text.trim();
    if (!content || busy) return;
    setError(false);
    setInput("");
    setFollowups([]);
    const history: Msg[] = [...messages, { role: "user", content }];
    setMessages([...history, { role: "assistant", content: "" }]);
    setBusy(true);
    track("help_chat_message_sent", { length: content.length });

    try {
      const res = await fetch("/api/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ messages: history, context: getChatContext() }),
      });
      if (!res.ok || !res.body) throw new Error(String(res.status));

      // Anthropic SSE passthrough: accumulate content_block_delta text.
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let answer = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          try {
            const ev = JSON.parse(line.slice(6));
            if (ev.type === "content_block_delta" && ev.delta?.text) {
              answer += ev.delta.text;
              setMessages([...history, { role: "assistant", content: splitFollowups(answer).text }]);
            }
          } catch {
            /* keepalives / non-JSON lines */
          }
        }
      }
      const parsed = splitFollowups(answer);
      if (!parsed.text) throw new Error("empty");
      setMessages([...history, { role: "assistant", content: parsed.text }]);
      setFollowups(parsed.followups);
    } catch {
      setError(true);
      setMessages(history);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className={`no-print fixed bottom-[calc(1.25rem+env(safe-area-inset-bottom))] right-5 z-50 flex flex-col items-end gap-3${
        formFocus && !open ? " max-lg:hidden" : ""
      }`}
    >
      {open && (
        <div className="flex h-[480px] w-[min(92vw,360px)] flex-col overflow-hidden rounded-2xl border border-line bg-white shadow-float">
          <div className="hero-navy flex items-center justify-between px-4 py-3 text-white">
            <div>
              <p className="font-display text-sm font-bold">RamsDocs assistant</p>
              <p className="text-[0.66rem] text-white/65">AI help — answers can be wrong. You remain the competent person.</p>
            </div>
            <button type="button" onClick={toggle} aria-label="Close chat" className="rounded-lg p-1 text-white/70 hover:bg-white/10 hover:text-white">
              <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M6 6l12 12M18 6 6 18" /></svg>
            </button>
          </div>

          <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto px-4 py-3">
            {messages.length === 0 && (
              <div>
                <p className="text-[0.82rem] text-body">Hi — ask me anything about RamsDocs, the report builder or RAMS documents.</p>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {starters.map((s) => (
                    <button key={s} type="button" onClick={() => send(s)} className="rounded-full border border-line px-3 py-1.5 text-[0.74rem] font-medium text-body hover:border-emerald/50 hover:text-ink">
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={m.role === "user" ? "flex justify-end" : "flex justify-start"}>
                <div
                  className={`max-w-[85%] whitespace-pre-wrap rounded-2xl px-3.5 py-2 text-[0.82rem] leading-relaxed ${
                    m.role === "user" ? "bg-emerald text-white" : "bg-mist text-body"
                  }`}
                >
                  {m.content || <span className="inline-block animate-pulse">…</span>}
                </div>
              </div>
            ))}
            {!busy && followups.length > 0 && (
              <div className="flex flex-wrap gap-1.5 pt-0.5">
                {followups.map((q) => (
                  <button key={q} type="button" onClick={() => send(q)} className="rounded-full border border-emerald/40 bg-emerald-tint/40 px-3 py-1.5 text-left text-[0.74rem] font-medium text-emerald-deep hover:border-emerald hover:bg-emerald-tint">
                    {q}
                  </button>
                ))}
              </div>
            )}
            {error && <p className="text-[0.74rem] text-risk-veryhigh">Something went wrong — try again, or email hello@ramsdocs.co.uk.</p>}
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              void send(input);
            }}
            className="flex items-center gap-2 border-t border-line px-3 py-2.5"
          >
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask a question…"
              maxLength={2000}
              className="w-full rounded-full border border-line bg-white px-3.5 py-2 text-[0.82rem] text-ink outline-none placeholder:text-muted/60 focus:border-emerald"
            />
            <button
              type="submit"
              disabled={busy || !input.trim()}
              aria-label="Send"
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-emerald text-white transition-colors hover:bg-emerald-deep disabled:opacity-50"
            >
              <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h13M13 6l6 6-6 6" /></svg>
            </button>
          </form>
        </div>
      )}

      <button
        type="button"
        onClick={toggle}
        aria-label={open ? "Close help chat" : "Open help chat"}
        className="flex h-13 w-13 items-center justify-center rounded-full bg-emerald p-3.5 text-white shadow-[0_10px_24px_-8px_rgba(27,163,123,0.8)] transition-transform hover:scale-105"
      >
        {open ? (
          <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M6 9l6 6 6-6" /></svg>
        ) : (
          <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 12a8 8 0 0 1-8 8H4l1.6-3.2A8 8 0 1 1 21 12z" strokeLinejoin="round" /><path d="M8.5 10.5h7M8.5 13.5h4.5" strokeLinecap="round" /></svg>
        )}
      </button>
    </div>
  );
}
